import React, { useState } from 'react';
import { generateKoan, evaluateKoanAnswer } from '../services/geminiService';
import { ZenKoan, KoanEvaluation } from '../types';
import { Gavel, Send, RefreshCw, Feather, AlertTriangle } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { db } from '../firebase';
import { addDoc, collection } from 'firebase/firestore';

const ZenKoanPage: React.FC = () => {
  const [koan, setKoan] = useState<ZenKoan | null>(null);
  const [answer, setAnswer] = useState('');
  const [evaluation, setEvaluation] = useState<KoanEvaluation | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp, unlockBadge, user } = useUser();

  const handleFetchKoan = async () => {
    setLoading(true);
    setKoan(null);
    setEvaluation(null);
    setAnswer('');
    try {
      const data = await generateKoan();
      setKoan(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!koan || !answer.trim()) return;
    setLoading(true);
    try {
      const result = await evaluateKoanAnswer(koan.text, answer);
      setEvaluation(result);
      
      // Save history if logged in
      if (user.uid) {
          try {
              await addDoc(collection(db, 'users', user.uid, 'koan_history'), {
                  timestamp: new Date().toISOString(),
                  koanTitle: koan.title,
                  koanText: koan.text,
                  question: koan.question,
                  userAnswer: answer,
                  feedback: result.feedback,
                  masteryLevel: result.masteryLevel
              });
          } catch (e) {
              console.error("Failed to save history", e);
          }
      }

      if (result.masteryLevel === 'Master') addXp(150, 'Zen Master Insight');
      else if (result.masteryLevel === 'Monk') addXp(50, 'Zen Monk Insight');
      else addXp(10, 'Zen Practice');

      unlockBadge('koan_solver');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-slate-50 text-slate-600 rounded-full mb-4">
          <Gavel size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">Koan Dojo</h1>
        <p className="text-nunchi-600">Challenge your logical mind with the paradoxical riddles of Zen.</p>
      </header>

      {!koan && !loading && (
        <div className="text-center py-10">
          <button 
            onClick={handleFetchKoan}
            className="bg-nunchi-800 text-white px-8 py-3 rounded-xl font-bold hover:bg-nunchi-900 transition-all shadow-lg flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={18} /> Enter the Dojo
          </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-serif italic text-slate-500">The Master is contemplating...</p>
        </div>
      )}

      {koan && !loading && !evaluation && (
        <div className="animate-fadeIn">
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-nunchi-200 mb-8 relative">
            <h2 className="text-2xl font-serif font-bold text-nunchi-800 mb-4 text-center">{koan.title}</h2>
            <div className="w-12 h-1 bg-slate-200 mx-auto mb-6"></div>
            <p className="text-lg text-nunchi-700 leading-relaxed font-serif italic mb-6 text-center">
              "{koan.text}"
            </p>
            <div className="bg-slate-50 p-4 rounded-lg text-slate-700 font-bold text-center border border-slate-100">
              {koan.question}
            </div>
          </div>

          <div className="space-y-4">
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Type your answer... (Don't just use logic)"
              className="w-full h-32 bg-white p-4 rounded-xl border border-nunchi-200 focus:ring-2 focus:ring-slate-200 outline-none resize-none"
            />
            <button
              onClick={handleSubmit}
              disabled={!answer.trim()}
              className="w-full bg-nunchi-800 text-white py-4 rounded-xl font-bold hover:bg-nunchi-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
            >
              <Send size={18} /> Submit to Master
            </button>
          </div>
        </div>
      )}

      {evaluation && (
        <div className="animate-fadeIn space-y-6">
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-nunchi-200 text-center">
            <div className="mb-4">
               {evaluation.masteryLevel === 'Master' && <div className="inline-block px-4 py-1 bg-amber-100 text-amber-700 font-bold rounded-full text-sm uppercase tracking-wider">Enlightened</div>}
               {evaluation.masteryLevel === 'Monk' && <div className="inline-block px-4 py-1 bg-blue-100 text-blue-700 font-bold rounded-full text-sm uppercase tracking-wider">Deep Insight</div>}
               {evaluation.masteryLevel === 'Grasshopper' && <div className="inline-block px-4 py-1 bg-gray-100 text-gray-600 font-bold rounded-full text-sm uppercase tracking-wider">Still Learning</div>}
            </div>
            
            <p className="text-xl font-serif text-nunchi-800 mb-6 leading-relaxed">
              "{evaluation.feedback}"
            </p>

            <button 
              onClick={handleFetchKoan}
              className="text-nunchi-500 hover:text-nunchi-800 font-bold underline flex items-center justify-center gap-2 mx-auto"
            >
              <RefreshCw size={16} /> Meditate on New Koan
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZenKoanPage;